import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, CheckCircle2 } from "lucide-react";
import api, { errMsg, imageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { buildWhatsappLinks } from "@/lib/whatsappLink";

/**
 * PrintableQuote — Phase 5. Read-only, print-friendly render of the
 * IMMUTABLE snapshot stored on the quote (and its linked booking).
 * NEVER recalculates pricing. NEVER adds GST/tax/service/delivery/discount.
 *
 * scope = "customer" → hits /api/quotes/:id + /api/bookings/:bid
 * scope = "admin"    → hits /api/admin/quotes/:id + /api/admin/bookings/:bid
 */
const CUR = "\u20b9";
const fmt = (n) =>
  n === null || n === undefined
    ? "—"
    : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Brand name: "Khana" in black, everything else in brand red.
const BrandName = ({ text }) => (
  <div className="p-name" data-testid="print-business-name">
    {String(text).split(/(khana)/i).map((part, i) =>
      /^khana$/i.test(part) ? (
        <span key={i} style={{ color: "#111111" }}>{part}</span>
      ) : (
        <span key={i} style={{ color: "#7a1e1e" }}>{part}</span>
      )
    )}
  </div>
);


const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load the payment gateway"));
    document.body.appendChild(s);
  });

export default function PrintableQuote({ scope = "customer" }) {
  const { id } = useParams();
  const { settings } = useApp();
  const [q, setQ] = useState(null);
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [paying, setPaying] = useState(null);

  const quoteUrl =
    scope === "admin"
      ? `/admin/quotes/${id}`
      : scope === "public"
        ? `/public/quotes/${id}`
        : `/quotes/${id}`;
  const bookingUrl = (bid) =>
    scope === "admin" ? `/admin/bookings/${bid}` : `/bookings/${bid}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(quoteUrl);
        setQ(data);
        if (data.booking_id && scope !== "public") {
          try {
            const { data: bk } = await api.get(bookingUrl(data.booking_id));
            setB(bk);
          } catch { /* booking read is best-effort */ }
        }
      } catch (e) {
        setError(errMsg(e, "Could not load quote"));
        toast.error(errMsg(e, "Could not load quote"));
      } finally {
        setLoading(false);
      }
    })();
    if (scope === "public") {
      api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => setPayCfg({ enabled: false }));
    }
    // eslint-disable-next-line
  }, [id, scope]);

  const doPrint = () => window.print();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl p-8 text-sm text-muted-foreground" data-testid="print-loading">
        Loading printable quote…
      </div>
    );
  }
  if (error || !q) {
    return (
      <div className="mx-auto max-w-3xl p-8 text-sm text-destructive" data-testid="print-error">
        {error || "Quote not found."}
      </div>
    );
  }

  // Guest public link: never show ₹0 for a quote that hasn't been priced yet.
  // Exception: a server-computed estimate (package × guests) may be shown as
  // an estimated subtotal while the formal pricing is finalised.
  const hasEstimate = q.pricing_snapshot?.subtotal != null && Number(q.pricing_snapshot.subtotal) > 0;
  if (scope === "public" && q.final_amount == null && !hasEstimate) {
    return (
      <div className="grid min-h-screen place-items-center bg-white p-8" data-testid="print-pending-page">
        <div style={{ maxWidth: 520, textAlign: "center", fontFamily: 'Georgia, "Times New Roman", serif' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#7a1e1e", letterSpacing: "0.06em" }}>TEZZ KHANA</div>
          <div style={{ fontSize: 10, color: "#6b6b6b", textTransform: "uppercase", letterSpacing: "0.2em" }}>Catering Service</div>
          <div style={{ marginTop: 24, border: "1px solid #ecd9a8", background: "#fdf7e7", borderRadius: 12, padding: "28px 24px" }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#7a1e1e" }}>Your quotation is being prepared</h1>
            <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6, color: "#5b4a1e" }}>
              Our team is finalising the pricing for your event. Please check back shortly —
              this same link will show your complete quotation once it&apos;s ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const business = settings?.business_name || "TEZZ KHANA CATERING SERVICE";
  const logoSrc = settings?.logo_image_id ? imageUrl(settings.logo_image_id) : null;
  const subtotal =
    q.pricing_snapshot?.subtotal ?? q.pricing_snapshot?.subtotal_partial ?? 0;
  const additional = q.additional_charges || [];
  const finalAmount = q.final_amount != null ? q.final_amount : subtotal;
  const advance = q.advance_requested;
  const payInfo = b || (scope === "public" ? q?.payment_summary : null) || null;
  const amountPaid = payInfo?.amount_paid ?? 0;
  const remaining = Math.max(0, Number(finalAmount || 0) - Number(amountPaid || 0));

  // Terms snapshot: prefer the frozen booking snapshot (Phase 3), fall back to live settings.
  const terms =
    b?.terms_accepted_snapshot ||
    settings?.terms_and_conditions ||
    "";

  const payOnline = async (purposeKind) => {
    if (!q?.customer_confirmed_at) {
      toast.error("Please confirm the quote details first");
      return;
    }
    setPaying(purposeKind);
    try {
      await loadRazorpayScript();
      const { data } = await api.post("/payments/public/initiate", { quote_id: id, purpose: purposeKind });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Tezz Khana Catering",
        description: `Booking ${data.booking_reference} — ${purposeKind === "advance" ? "Advance payment" : "Full payment"}`,
        order_id: data.order_id,
        prefill: {
          name: q?.customer_name || q?.name || "",
          contact: q?.mobile || "",
        },
        theme: { color: "#b45309" },
        modal: { ondismiss: () => setPaying(null) },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/payments/public/verify", {
              quote_id: id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setQ((prev) => ({
              ...prev,
              payment_summary: v.payment_summary,
              customer_confirmed: true,
              customer_confirmed_at: prev.customer_confirmed_at || new Date().toISOString(),
            }));
            toast.success("Payment received — thank you!");
          } catch (e) {
            toast.error(errMsg(e, "Payment verification failed — please contact us on WhatsApp"));
          } finally {
            setPaying(null);
          }
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed — no amount was captured. You can try again.");
        setPaying(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e, "Could not start the payment"));
      setPaying(null);
    }
  };

  const confirmQuote = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/public/quotes/${id}/confirm`);
      setQ((prev) => ({
        ...prev,
        customer_confirmed: true,
        customer_confirmed_at: data.customer_confirmed_at,
      }));
      toast.success("Quote confirmed — thank you!");
      const msg = `I confirm this quote — Quotation No. #${(q.id || "").slice(0, 8).toUpperCase()}, Total ${fmt(finalAmount)}. Please proceed.`;
      const links = buildWhatsappLinks(settings, msg);
      if (links.length) window.open(links[0].url, "_blank", "noopener");
    } catch (e) {
      toast.error(errMsg(e, "Could not confirm the quote"));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="printable-root bg-white text-black" data-testid="print-root">
      <style>{`
        @page { size: A4; margin: 16mm; }
        @media print {
          .no-print { display: none !important; }
          .printable-root { background: white !important; }
        }
        .printable-root { font-family: Georgia, "Times New Roman", serif; color: #111; }
        .printable-root h1, .printable-root h2, .printable-root h3 { font-family: inherit; }
        .p-eyebrow { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: #6b6b6b; }
        .p-value { font-weight: 600; color: #111; }
        .p-section { border: 1px solid #d9d5cf; border-radius: 8px; padding: 14px 16px; margin-top: 12px; page-break-inside: avoid; }
        .p-row { display: flex; justify-content: space-between; align-items: baseline; padding: 4px 0; font-size: 12px; }
        .p-row strong { color: #111; }
        .p-hr { border: 0; border-top: 1px solid #d9d5cf; margin: 8px 0; }
        .p-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #7a1e1e; padding-bottom: 8px; }
        .p-brand { display: flex; align-items: center; gap: 12px; }
        .p-brand img { height: 60px; width: auto; }
        .p-brand .p-name { font-size: 18px; font-weight: 700; color: #7a1e1e; letter-spacing: 0.06em; }
        .p-brand .p-tag { font-size: 10px; color: #6b6b6b; text-transform: uppercase; letter-spacing: 0.2em; }
        .p-meta { text-align: right; font-size: 11px; color: #333; }
        .p-meta .p-ref { font-weight: 700; color: #111; font-size: 14px; }
        .p-toolbar { display: flex; justify-content: flex-end; padding: 12px 0; }
        .p-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: #7a1e1e; color: #fff; border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer; border: 0; }
        table.p-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
        table.p-table th, table.p-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e6e2dc; vertical-align: top; }
        table.p-table th { background: #f6f2ec; text-transform: uppercase; letter-spacing: 0.12em; font-size: 10px; color: #6b6b6b; }
        .p-total { font-size: 16px; font-weight: 700; color: #7a1e1e; }
        .p-terms { white-space: pre-wrap; font-size: 10px; line-height: 1.55; color: #333; max-height: none; }
        .p-status-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; background: #f3ede3; color: #7a1e1e; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; }
        .p-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .p-generated { text-align: right; font-size: 10px; color: #6b6b6b; margin-top: 4px; }
      `}</style>

      <div className="mx-auto max-w-3xl px-6 pb-16 pt-6">
        <div className="no-print p-toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
          {scope === "public" &&
            (q.customer_confirmed_at ? (
              <span
                data-testid="public-confirmed-badge"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#e8f5ec", color: "#1a7a3a", borderRadius: 999, fontSize: 12, fontWeight: 700 }}
              >
                <CheckCircle2 size={14} /> Confirmed on {new Date(q.customer_confirmed_at).toLocaleDateString("en-IN")}
              </span>
            ) : (
              <button
                data-testid="public-confirm-quote-btn"
                className="p-btn"
                style={{ background: "#1a7a3a" }}
                onClick={confirmQuote}
                disabled={confirming}
              >
                <CheckCircle2 size={14} /> {confirming ? "Confirming…" : "I have reviewed & confirm these details"}
              </button>
            ))}
          {scope === "public" && payCfg?.enabled && q.final_amount != null && amountPaid < Number(q.final_amount) && (
            <>
              {Number(q.advance_requested) > 0 && amountPaid < Number(q.advance_requested) && (
                <button
                  data-testid="public-pay-advance-btn"
                  className="p-btn"
                  style={{ background: "#0b5cab", opacity: q.customer_confirmed_at ? 1 : 0.45, cursor: q.customer_confirmed_at ? "pointer" : "not-allowed" }}
                  disabled={!!paying || !q.customer_confirmed_at}
                  onClick={() => payOnline("advance")}
                >
                  {paying === "advance" ? "Opening checkout…" : `Pay ${fmt(Number(q.advance_requested) - amountPaid)} Advance`}
                </button>
              )}
              <button
                data-testid="public-pay-full-btn"
                className="p-btn"
                style={{ background: "#0b5cab", opacity: q.customer_confirmed_at ? 1 : 0.45, cursor: q.customer_confirmed_at ? "pointer" : "not-allowed" }}
                disabled={!!paying || !q.customer_confirmed_at}
                onClick={() => payOnline("full")}
              >
                {paying === "full" ? "Opening checkout…" : `Pay ${fmt(Number(q.final_amount) - amountPaid)} Full Amount`}
              </button>
            </>
          )}
          <button data-testid="print-button" className="p-btn" onClick={doPrint}>
            <Printer size={14} /> Print / Save as PDF
          </button>
          {scope === "public" && payCfg?.enabled && !q.customer_confirmed_at && q.final_amount != null && amountPaid < Number(q.final_amount) && (
            <div
              data-testid="public-confirm-required-hint"
              style={{ width: "100%", textAlign: "right", fontSize: 11, color: "#7a1e1e", fontWeight: 600 }}
            >
              Please review the details below and tap "I have reviewed &amp; confirm these details" to enable payment.
            </div>
          )}
        </div>

        <header className="p-header" data-testid="print-header">
          <div className="p-brand">
            {logoSrc ? (
              <img src={logoSrc} alt={business} data-testid="print-logo-image" />
            ) : (
              <div data-testid="print-logo-fallback">
                <BrandName text="TEZZ KHANA" />
                <div className="p-tag">Catering Service</div>
              </div>
            )}
            {logoSrc && (
              <div>
                <BrandName text={business} />
                <div className="p-tag">Owner-approved Terms &amp; Conditions apply</div>
              </div>
            )}
          </div>
          <div className="p-meta">
            <div className="p-eyebrow">Quote</div>
            <div className="p-ref" data-testid="print-quote-ref">
              #{(q.id || "").slice(0, 8).toUpperCase()}
            </div>
            {b?.reference && (
              <div className="p-eyebrow" data-testid="print-booking-ref">
                Booking {b.reference}
              </div>
            )}
            <div className="p-generated" data-testid="print-generated-at">
              Generated {new Date().toISOString().slice(0, 16).replace("T", " ")} UTC
            </div>
          </div>
        </header>

        {/* Status + parties */}
        <section className="p-section" data-testid="print-status-section">
          <div className="p-eyebrow">Status</div>
          <div className="mt-1">
            <span className="p-status-pill" data-testid="print-quote-status">{q.status}</span>
            {b?.status && (
              <span className="p-status-pill ml-2" data-testid="print-booking-status" style={{ marginLeft: 8 }}>
                Booking {b.status}
              </span>
            )}
          </div>
        </section>

        <div className="p-two-col">
          <section className="p-section" data-testid="print-customer-section">
            <div className="p-eyebrow">Customer</div>
            <div className="p-row"><span>Name</span><span className="p-value" data-testid="print-customer-name">{q.customer_name || "—"}</span></div>
            <div className="p-row"><span>Mobile</span><span className="p-value">{q.mobile || "—"}</span></div>
            {q.whatsapp && <div className="p-row"><span>WhatsApp</span><span className="p-value">{q.whatsapp}</span></div>}
            {q.email && <div className="p-row"><span>Email</span><span className="p-value">{q.email}</span></div>}
          </section>

          <section className="p-section" data-testid="print-event-section">
            <div className="p-eyebrow">Event</div>
            <div className="p-row"><span>Type</span><span className="p-value">{q.event_type || "—"}</span></div>
            <div className="p-row"><span>Date</span><span className="p-value">{q.event_date || "—"}</span></div>
            <div className="p-row"><span>Time</span><span className="p-value">{q.event_time || "—"}</span></div>
            <div className="p-row"><span>Guests</span><span className="p-value" data-testid="print-guests">{q.guest_count || "—"}</span></div>
            <div className="p-row"><span>Venue</span><span className="p-value" style={{ textAlign: "right", maxWidth: 220 }}>{q.venue || "—"}</span></div>
          </section>
        </div>

        {q.package_snapshot && (
          <section className="p-section" data-testid="print-package-section">
            <div className="p-eyebrow">Package (frozen)</div>
            <div className="p-row">
              <span><strong data-testid="print-package-name">{q.package_snapshot.name}</strong>
                {q.package_snapshot.tier && (<span className="p-tag" style={{ marginLeft: 8 }}>{String(q.package_snapshot.tier).toUpperCase()}</span>)}
              </span>
              <span className="p-value" data-testid="print-package-rate">
                {fmt(q.package_snapshot.price)}{q.package_snapshot.pricing_unit ? ` / ${q.package_snapshot.pricing_unit}` : ""}
              </span>
            </div>
            {q.package_snapshot.minimum_guests != null && (
              <div className="p-row"><span>Minimum guests</span><span className="p-value">{q.package_snapshot.minimum_guests}</span></div>
            )}
          </section>
        )}

        {q.dishes_snapshot?.length > 0 && (
          <section className="p-section" data-testid="print-dishes-section">
            <div className="p-eyebrow">Selected dishes ({q.dishes_snapshot.length})</div>
            <table className="p-table">
              <thead><tr><th>Dish</th><th>Category</th></tr></thead>
              <tbody>
                {q.dishes_snapshot.map((d, i) => (
                  <tr key={d.id || i} data-testid={`print-dish-${i}`}>
                    <td>{d.name}</td>
                    <td>{d.category_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {q.extras_snapshot?.length > 0 && (
          <section className="p-section" data-testid="print-extras-section">
            <div className="p-eyebrow">Extras</div>
            <table className="p-table">
              <thead><tr><th>Category</th><th>Qty</th><th>Rate / head</th></tr></thead>
              <tbody>
                {q.extras_snapshot.map((e, i) => (
                  <tr key={i} data-testid={`print-extra-${i}`}>
                    <td>{e.category_name || "—"}</td>
                    <td>{e.qty}</td>
                    <td>{e.rate_per_head != null ? fmt(e.rate_per_head) : "TO BE CONFIGURED"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <section className="p-section" data-testid="print-pricing-section">
          <div className="p-eyebrow">Pricing</div>
          {q.final_amount == null && !hasEstimate ? (
            <div data-testid="print-pricing-pending" style={{ fontSize: 12, padding: "8px 0", color: "#5b4a1e" }}>
              Pricing is being finalised — the complete amount will appear here once
              confirmed by our team.
            </div>
          ) : (
          <>
          <div className="p-row">
            <span>Base package amount</span>
            <span className="p-value" data-testid="print-package-amount">{fmt(q.pricing_snapshot?.package_amount)}</span>
          </div>
          {q.pricing_snapshot?.extras_amount != null && q.pricing_snapshot.extras_amount > 0 && (
            <div className="p-row">
              <span>Extras</span>
              <span className="p-value" data-testid="print-extras-amount">{fmt(q.pricing_snapshot.extras_amount)}</span>
            </div>
          )}
          <div className="p-row">
            <span><strong>{q.final_amount == null ? "Estimated subtotal" : "Subtotal"}</strong></span>
            <span className="p-value" data-testid="print-subtotal">{fmt(subtotal)}</span>
          </div>

          {additional.length > 0 && (
            <>
              <hr className="p-hr" />
              <div className="p-eyebrow">Additional charges</div>
              <table className="p-table" data-testid="print-charges-table">
                <thead><tr><th>Label</th><th style={{ textAlign: "right" }}>Amount</th><th>Note</th></tr></thead>
                <tbody>
                  {additional.map((c, i) => (
                    <tr key={i} data-testid={`print-charge-row-${i}`}>
                      <td data-testid={`print-charge-label-${i}`}>{c.label}</td>
                      <td style={{ textAlign: "right" }} data-testid={`print-charge-amount-${i}`}>{fmt(c.amount)}</td>
                      <td data-testid={`print-charge-note-${i}`}>{c.note || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <hr className="p-hr" />
          {q.gst_snapshot?.enabled && (
            <>
              <div className="p-row">
                <span>Taxable subtotal</span>
                <span className="p-value" data-testid="print-gst-taxable">{fmt(q.gst_snapshot.taxable_subtotal)}</span>
              </div>
              <div className="p-row">
                <span>GST ({q.gst_snapshot.rate}%)</span>
                <span className="p-value" data-testid="print-gst-amount">{fmt(q.gst_snapshot.gst_amount)}</span>
              </div>
            </>
          )}
          {q.final_amount != null ? (
            <div className="p-row p-total">
              <span>Final total</span>
              <span data-testid="print-final-amount">{fmt(finalAmount)}</span>
            </div>
          ) : (
            <div data-testid="print-final-pending" style={{ fontSize: 12, padding: "8px 0", color: "#5b4a1e" }}>
              Final total (including GST and any additional charges) will be confirmed by our team.
            </div>
          )}
          {advance != null && (
            <div className="p-row">
              <span>Advance requested (₹, explicit — not a percentage)</span>
              <span className="p-value" data-testid="print-advance">{fmt(advance)}</span>
            </div>
          )}
          {payInfo && (Number(payInfo.amount_paid) > 0 || (payInfo.payments || []).length > 0) && (
            <>
              <div className="p-row">
                <span>Amount paid</span>
                <span className="p-value" data-testid="print-amount-paid">{fmt(amountPaid)}</span>
              </div>
              <div className="p-row">
                <span>Remaining balance</span>
                <span className="p-value" data-testid="print-remaining">{fmt(remaining)}</span>
              </div>
              <div className="p-row">
                <span>Payment status</span>
                <span className="p-value" data-testid="print-payment-status">{payInfo.payment_status}</span>
              </div>
            </>
          )}
          {(!payInfo || (Number(payInfo.amount_paid) === 0 && (payInfo.payments || []).length === 0)) && advance != null && (
            <div className="p-row">
              <span>Remaining balance</span>
              <span className="p-value" data-testid="print-remaining">{fmt(remaining)}</span>
            </div>
          )}
          </>
          )}
        </section>

        {terms && (
          <section className="p-section" data-testid="print-terms-section">
            <div className="p-eyebrow">
              Terms &amp; Conditions {b?.terms_accepted_snapshot ? "(frozen at approval)" : ""}
            </div>
            {b?.terms_accepted_at && (
              <div className="p-eyebrow" data-testid="print-terms-accepted-at" style={{ marginTop: 2 }}>
                Accepted {String(b.terms_accepted_at).slice(0, 16).replace("T", " ")} UTC
              </div>
            )}
            <div className="p-terms" data-testid="print-terms-body">{terms}</div>
          </section>
        )}

        <section className="p-section" data-testid="print-approval-section">
          <div className="p-eyebrow">Customer Approval</div>
          <div className="p-row"><span>Customer Name</span><span className="p-value" data-testid="print-approval-name">{q.customer_name || "—"}</span></div>
          <div className="p-row"><span>Event Type</span><span className="p-value" data-testid="print-approval-event-type">{q.event_type || "—"}</span></div>
          <div className="p-row"><span>Event Date</span><span className="p-value" data-testid="print-approval-event-date">{q.event_date || ""}</span></div>
          <div className="p-row"><span>Venue</span><span className="p-value" data-testid="print-approval-venue" style={{ textAlign: "right", maxWidth: 220 }}>{q.venue || ""}</span></div>
          <div className="p-row"><span>Guests</span><span className="p-value" data-testid="print-approval-guests">{q.guest_count || "—"}</span></div>
          <div className="p-row"><span>Package</span><span className="p-value" data-testid="print-approval-package">{q.package_snapshot?.name || (q.selected_menu || []).find((m) => m.kind === "package")?.name || ""}</span></div>
          <div className="p-row"><span>Quotation No.</span><span className="p-value" data-testid="print-approval-ref">#{(q.id || "").slice(0, 8).toUpperCase()}</span></div>
          <div style={{ display: "flex", gap: 32, marginTop: 22 }} data-testid="print-approval-signature-row">
            <div style={{ flex: 2 }}>
              {q.customer_confirmed_at ? (
                <div
                  data-testid="print-approval-signed-digital"
                  style={{ borderBottom: "1px solid #000", height: 30, display: "flex", alignItems: "flex-end", paddingBottom: 2, fontWeight: 700, color: "#1a7a3a" }}
                >
                  ✓ Confirmed digitally by {q.customer_name}
                </div>
              ) : (
                <div style={{ borderBottom: "1px solid #000", height: 30 }} />
              )}
              <div className="p-eyebrow" style={{ marginTop: 4 }}>Customer Signature</div>
            </div>
            <div style={{ flex: 1 }}>
              {q.customer_confirmed_at ? (
                <div
                  data-testid="print-approval-signed-date"
                  style={{ borderBottom: "1px solid #000", height: 30, display: "flex", alignItems: "flex-end", paddingBottom: 2, fontWeight: 600 }}
                >
                  {new Date(q.customer_confirmed_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                </div>
              ) : (
                <div style={{ borderBottom: "1px solid #000", height: 30 }} />
              )}
              <div className="p-eyebrow" style={{ marginTop: 4 }}>Date</div>
            </div>
          </div>
        </section>

        <div className="p-generated" data-testid="print-footer">
          {business} · Printable quote generated from the immutable Tezz Khana snapshot. Applicable government charges, decoration, transportation, live counters and special equipment are billed only per the written Terms &amp; Conditions.
        </div>
      </div>
    </div>
  );
}
